import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { PasswordInput } from '@ui/password-input.tsx';
import { Button } from '@ui/button.tsx';
import { Save } from 'lucide-react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePasswordUpdate } from '@r/api/generated/client.ts';
import { getOrvalErrorMessage } from '@r/api/orval-error-helper.ts';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useUser } from '@r/api/auth.tsx';
import { PASSWORD_REGEX } from '@r/utils/regex/password.ts';
import type { FC } from 'react';

type UpdatePasswordFormProps = {
  variant?: 'admin' | 'client';
};

export const UpdatePasswordForm: FC<UpdatePasswordFormProps> = ({ variant }) => {
  const { t } = useTranslation();
  const user = useUser();

  const baseSchema = z.object({
    currentPassword: z.string().regex(PASSWORD_REGEX, {
      message: t('form.password.invalid')
    }),
    newPassword: z.string().regex(PASSWORD_REGEX, {
      message: t('form.password.invalid')
    }),
    passwordConfirmation: z.string().regex(PASSWORD_REGEX, {
      message: t('form.password.invalid')
    })
  });

  const updatePasswordSchema = baseSchema
    .refine((data) => data.newPassword === data.passwordConfirmation, {
      path: ['passwordConfirmation'],
      message: t('auth.password.confirmation.invalid')
    })
    .superRefine((data, ctx) => {
      if (data.currentPassword === data.newPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('form.password.current.new.error'),
          path: ['newPassword']
        });
      }
    });

  const form = useForm<z.infer<typeof updatePasswordSchema>>({
    resolver: zodResolver(updatePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      passwordConfirmation: ''
    }
  });

  const passwordMutation = usePasswordUpdate({
    mutation: {
      onSuccess: () => {
        toast.success(t('form.password.success'));
        user.refetch();
        form.reset();
      },
      onError: (error: unknown) => {
        const message = getOrvalErrorMessage(error);
        if (message === 'Current password is incorrect') {
          form.setError(
            'currentPassword',
            { message: t('form.password.current.error') },
            { shouldFocus: true }
          );
        } else {
          toast.error(t('form.password.error'));
        }
      }
    }
  });

  function onSubmit(data: z.infer<typeof updatePasswordSchema>) {
    passwordMutation.mutate({
      data: {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword
      }
    });
  }

  return (
    <Form {...form} schema={baseSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className={`grid grid-cols-1 gap-6 ${variant === 'client' ? 'md:grid-cols-2' : ''}`}>
          <FormField
            control={form.control}
            name="currentPassword"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('form.password.current.label')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder={t('form.password.current.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="newPassword"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('form.password.new.label')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder={t('form.password.new.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className={`grid grid-cols-1 gap-6 ${variant === 'client' ? 'md:grid-cols-2' : ''}`}>
          <FormField
            control={form.control}
            name="passwordConfirmation"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('form.password.confirm.label')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder={t('form.password.confirm.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button
          rounded="full"
          loading={passwordMutation.isPending}
          className="w-full md:w-auto"
          type="submit"
        >
          <Save className="mr-2 w-4 h-4" />
          {t('form.password.update')}
        </Button>
      </form>
    </Form>
  );
};
